import * as Primitive from '@radix-ui/react-dropdown-menu';
import { Property } from 'csstype';
import {
  Children,
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  FC,
  forwardRef,
  FunctionComponentElement,
  Ref,
} from 'react';
import styled from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import Menu, {
  MenuDivider,
  MenuHeading,
  MenuItem,
  MenuItemProps,
} from 'design-system/components/Menu/Menu';
import { BASE } from 'design-system/variables/spacing';

const DropdownMenuContent = styled(Primitive.Content)<{ $zIndex?: Property.ZIndex }>`
  z-index: ${({ $zIndex }) => $zIndex || Z_INDICES.tooltip};
`;

export interface DropdownMenuProps
  extends Primitive.DropdownMenuProps,
    Pick<Primitive.DropdownMenuContentProps, 'side' | 'align'> {
  id?: string;
  className?: string;

  trigger: FunctionComponentElement<any>;

  /**
   * Props passed to internal DropdownMenu.Content component.
   */
  contentProps?: Primitive.DropdownMenuContentProps;

  /**
   * Props passed to internal DropdownMenu.Trigger component.
   */
  triggerProps?: Primitive.DropdownMenuTriggerProps;

  /**
   * Props passed to internal DropdownMenu.Portal component.
   */
  portalProps?: Primitive.DropdownMenuPortalProps;

  /**
   * Whether or not to wrap menu with DropdownMenu.Portal component.
   */
  portal?: boolean;

  /**
   * Whether or not clicks render menu items.
   */
  disabled?: boolean;

  zIndex?: Property.ZIndex;
}

const DropdownMenu = ({
  trigger,
  children,
  side,
  align = 'end',
  contentProps,
  triggerProps,
  portalProps,
  className,
  portal,
  modal = false,
  disabled = false,
  zIndex,
  ...props
}: DropdownMenuProps) => {
  if (Children.count(children) === 0) return null;

  const content = disabled ? null : (
    <DropdownMenuContent
      sideOffset={BASE}
      collisionPadding={BASE}
      align={align}
      side={side}
      $zIndex={zIndex}
      {...contentProps}
    >
      <Menu className={className}>{children}</Menu>
    </DropdownMenuContent>
  );

  return (
    <Primitive.Root modal={modal} {...props}>
      <Primitive.Trigger asChild {...triggerProps}>
        {trigger}
      </Primitive.Trigger>

      {portal ? <Primitive.Portal {...portalProps}>{content}</Primitive.Portal> : content}
    </Primitive.Root>
  );
};

interface DropdownMenuItemProps<T extends ElementType = 'div'>
  extends MenuItemProps<T>,
    Omit<Primitive.DropdownMenuItemProps, 'asChild' | 'children'> {}

interface DropdownMenuItemType<T extends ElementType = 'div'>
  extends FC<DropdownMenuItemProps<T> & ComponentPropsWithRef<T>> {
  <T extends ElementType = 'div'>(
    props: DropdownMenuItemProps<T> & ComponentPropsWithoutRef<T>,
  ): ReturnType<FC<DropdownMenuItemProps<T> & ComponentPropsWithoutRef<T>>>;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const DropdownMenuItem: DropdownMenuItemType = forwardRef(function DropdownMenuItem(
  { children, disabled, ...props },
  ref: Ref<HTMLDivElement>,
) {
  return (
    <Primitive.Item asChild disabled={disabled} {...props}>
      <MenuItem disabled={disabled} ref={ref}>
        {children}
      </MenuItem>
    </Primitive.Item>
  );
});

export default Object.assign(DropdownMenu, {
  Item: DropdownMenuItem,
  Heading: MenuHeading,
  Divider: MenuDivider,
});
